import React, { createContext, useContext, useState } from 'react';

export type ComposerBlocksContextType = {
  disabledColumns: boolean;
  setDisabledColumns: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ComposerBlocksContext = createContext<
  ComposerBlocksContextType | undefined
>(undefined);

/**
 * UseComposerBlocksContext is a custom hook that provides access to the ComposerBlocksContext.
 * It allows components to consume the context values without needing to use the Context.Consumer directly..
 * @returns {Object} The context value, which includes state and functions related to composer blocks.
 */
export const useComposerBlocksContext = () => useContext(ComposerBlocksContext);

/**
 * ComposerBlocksProvider is a context provider for managing state related to composer blocks.
 * It provides a way to share state across different components in the composer.
 * @param {Object} props - The props for the provider.
 * @param {React.ReactNode} props.children - The child components that will have access to the context.
 */
export const ComposerBlocksProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [disabledColumns, setDisabledColumns] = useState<boolean>(true);

  // Use useMemo to prevent unnecessary re-renders
  const contextValue = React.useMemo(
    () => ({
      disabledColumns,
      setDisabledColumns,
      // ...add more as needed
    }),
    [disabledColumns],
  );

  return (
    <ComposerBlocksContext.Provider value={contextValue}>
      {children}
    </ComposerBlocksContext.Provider>
  );
};
